"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ListboxOption = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _ListboxOptionLabel = require("../ListboxOptionLabel/ListboxOptionLabel");
var _ListboxOptionMedia = require("../ListboxOptionMedia/ListboxOptionMedia");
var _ListboxOptionMeta = require("../ListboxOptionMeta/ListboxOptionMeta");
var _ListboxOption = require("./ListboxOption.styles");
var _excluded = ["active", "appearance", "children", "disabled", "icon", "meta", "label", "onClick", "onMouseEnter", "onOptionClick", "onOptionHover", "selected", "value"];
/**
 * The default option component for a `Listbox`.
 *
 * **Note:** This component is not meant to be used outside the context of a
 * `Listbox` `renderOption` callback.
 *
 * @since 8.28.0
 */
var ListboxOption = /*#__PURE__*/(0, _react.forwardRef)(function (props, forwardedRef) {
  var active = props.active,
    appearance = props.appearance,
    children = props.children,
    disabled = props.disabled,
    icon = props.icon,
    meta = props.meta,
    label = props.label,
    onClick = props.onClick,
    onMouseEnter = props.onMouseEnter,
    onOptionClick = props.onOptionClick,
    onOptionHover = props.onOptionHover,
    selected = props.selected,
    value = props.value,
    rest = (0, _objectWithoutProperties2.default)(props, _excluded);
  var clickCallback = (0, _react.useCallback)(function (e) {
    if (onOptionClick && !disabled) {
      onOptionClick(value);
    }
    if (onClick) {
      onClick(e);
    }
  }, [value, onOptionClick, onClick, disabled]);
  var mouseEnterCallback = (0, _react.useCallback)(function (e) {
    if (onOptionHover && !disabled) {
      onOptionHover(value);
    }
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  }, [value, onOptionHover, onMouseEnter, disabled]);
  var hasMedia = icon || typeof appearance === 'string';
  var labelNode;
  var mediaNode;
  var metaNode;
  if (typeof children === 'undefined') {
    labelNode = /*#__PURE__*/_react.default.createElement(_ListboxOptionLabel.ListboxOptionLabel, null, label);
    mediaNode = hasMedia ? /*#__PURE__*/_react.default.createElement(_ListboxOptionMedia.ListboxOptionMedia, null, icon) : null;
    metaNode = meta ? /*#__PURE__*/_react.default.createElement(_ListboxOptionMeta.ListboxOptionMeta, null, meta) : null;
  }
  return /*#__PURE__*/_react.default.createElement(_ListboxOption.StyledListboxOption, (0, _extends2.default)({
    appearance: typeof appearance === 'string' ? appearance : undefined,
    "aria-disabled": disabled ? true : undefined,
    "aria-selected": selected ? true : undefined,
    "data-active": active,
    icon: icon,
    onClick: clickCallback,
    onMouseEnter: mouseEnterCallback,
    ref: forwardedRef
  }, rest), mediaNode, labelNode, metaNode, children);
});
exports.ListboxOption = ListboxOption;
ListboxOption.displayName = 'ListboxOption';
ListboxOption.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Marks this as the active option (the option that has focused).
   *
   * This is generally passed down from `Listbox`.
   */
  active: _propTypes.default.bool,
  /**
   * An optional appearance. If `icon` is also defined, `icon` will take precedence.
   */
  appearance: _propTypes.default.oneOf(['checkbox', 'radio']),
  /**
   * Set the option as disabled with the
   * [`aria-disabled`](https://www.w3.org/TR/wai-aria-1.2/#aria-disabled) attribute.
   */
  disabled: _propTypes.default.bool,
  /**
   * Set the option as selected with the
   * [`aria-selected`](https://www.w3.org/TR/wai-aria-1.2/#aria-selected) attribute.
   *
   * This will automatically be set when nested within
   * `Listbox`.
   */
  selected: _propTypes.default.bool,
  /**
   * In general you will want to use the `label` and `meta` props, but if you need full
   * customization of option content, you can provide your own node with `children`.
   */
  children: _propTypes.default.node,
  /**
   * An optional `Icon` node. This will take precedence
   * over the `appearance` prop.
   */
  icon: _propTypes.default.node,
  /**
   * Additional contextual information to display alongside the `label`.
   *
   * Like `label`, it is best to avoid long `meta` values to facilitate understandability for
   * screen reader users.
   */
  meta: _propTypes.default.string,
  /**
   * The label to display for this option.
   *
   * Avoiding very long option labels facilitates understandability and perceivability for screen
   * reader users. The entire label of an option is spoken as a single unit of speech when the
   * option is read.
   */
  label: _propTypes.default.node.isRequired,
  /**
   * @ignore
   */
  onClick: _propTypes.default.func,
  /**
   * Called when an option is clicked with the option value.
   */
  onOptionClick: _propTypes.default.func,
  /**
   * Called when an option is hovered with the option value.
   */
  onOptionHover: _propTypes.default.func,
  /**
   * @ignore
   */
  onMouseEnter: _propTypes.default.func,
  /**
   * https://www.w3.org/TR/wai-aria-1.2/#option
   */
  role: _propTypes.default.string,
  /**
   * The value of the option.
   */
  value: _propTypes.default.string.isRequired
} : {};
ListboxOption.defaultProps = {
  role: 'option'
};